// Currency utilities for multi-currency support

export interface Currency {
  code: string;
  symbol: string;
  name: string;
}

export interface ExchangeRate {
  from: string;
  to: string;
  rate: number;
  lastUpdated: Date;
}

// Supported currencies
export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
];

// Currency codes array for easy use in components
export const SUPPORTED_CURRENCY_CODES: string[] = SUPPORTED_CURRENCIES.map(c => c.code);

// Default exchange rates (simplified - in production, use real API)
const DEFAULT_EXCHANGE_RATES: Record<string, number> = {
  'USD_EUR': 0.85,
  'USD_GBP': 0.73,
  'USD_JPY': 110.0,
  'USD_CAD': 1.25,
  'USD_AUD': 1.35,
  'USD_CHF': 0.92,
  'USD_CNY': 6.45,
  'USD_INR': 75.0,
  'USD_BRL': 5.25,
  'USD_IDR': 16390.23, // 1 USD = ~16,390 IDR (current rate)
  'EUR_USD': 1.18,
  'EUR_GBP': 0.86,
  'EUR_JPY': 129.4,
  'EUR_CAD': 1.47,
  'EUR_AUD': 1.59,
  'EUR_CHF': 1.08,
  'EUR_CNY': 7.59,
  'EUR_INR': 88.2,
  'EUR_BRL': 6.18,
  'EUR_IDR': 19340.47, // 1 EUR = ~19,340 IDR (current rate)
  'GBP_USD': 1.37,
  'GBP_EUR': 1.16,
  'GBP_JPY': 150.7,
  'GBP_CAD': 1.71,
  'GBP_AUD': 1.85,
  'GBP_CHF': 1.26,
  'GBP_CNY': 8.84,
  'GBP_INR': 102.7,
  'GBP_BRL': 7.19,
  'GBP_IDR': 22454.62, // 1 GBP = ~22,455 IDR (current rate)
  'IDR_USD': 0.000061, // 1 IDR = ~0.000061 USD (1/16390.23)
  'IDR_EUR': 0.000052, // 1 IDR = ~0.000052 EUR (1/19340.47)
  'IDR_GBP': 0.000045, // 1 IDR = ~0.000045 GBP (1/22454.62)
};

// Get currency symbol by code
export const getCurrencySymbol = (code: string): string => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === code);
  return currency?.symbol || code;
};

// Get currency name by code
export const getCurrencyName = (code: string): string => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === code);
  return currency?.name || code;
};

// Get exchange rate with caching for real-time rates

export const getExchangeRate = (fromCurrency: string, toCurrency: string): number => {
  if (fromCurrency === toCurrency) return 1;
  
  const rateKey = `${fromCurrency}_${toCurrency}`;
  const reverseRateKey = `${toCurrency}_${fromCurrency}`;
  
  // Try direct rate
  if (DEFAULT_EXCHANGE_RATES[rateKey]) {
    return DEFAULT_EXCHANGE_RATES[rateKey];
  }
  
  // Try reverse rate
  if (DEFAULT_EXCHANGE_RATES[reverseRateKey]) {
    return 1 / DEFAULT_EXCHANGE_RATES[reverseRateKey];
  }
  
  // Fallback to USD conversion
  if (fromCurrency !== 'USD' && toCurrency !== 'USD') {
    const fromToUSD = getExchangeRate(fromCurrency, 'USD');
    const usdToTo = getExchangeRate('USD', toCurrency);
    return fromToUSD * usdToTo;
  }
  
  // Default fallback
  return 1;
};

// Convert amount from one currency to another
export const convertCurrency = (
  amount: number, 
  fromCurrency: string, 
  toCurrency: string
): number => {
  const rate = getExchangeRate(fromCurrency, toCurrency);
  return amount * rate;
};

// Format currency amount
export const formatCurrency = (
  amount: number, 
  currencyCode: string, 
  options?: Intl.NumberFormatOptions
): string => {
  // Handle invalid currency codes
  const validCurrencyCode = currencyCode && currencyCode.trim() ? currencyCode : 'USD';
  
  // Use Intl.NumberFormat for proper formatting
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: validCurrencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  });
  
  return formatter.format(amount);
};

// Get monthly cost in user's preferred currency
export const getMonthlyCostInCurrency = (
  subscription: { cost: number; currency?: string; billing_cycle: string },
  userPreferredCurrency: string
): number => {
  // Convert to monthly cost first
  let monthlyCost = subscription.cost;
  
  switch (subscription.billing_cycle) {
    case 'yearly':
      monthlyCost = subscription.cost / 12;
      break;
    case 'every_6_months':
      monthlyCost = subscription.cost / 6;
      break;
    case 'bimonthly':
      monthlyCost = subscription.cost / 2;
      break;
    case 'every_90_days':
      monthlyCost = subscription.cost * (365 / 90) / 12; // Convert 90-day cycle to monthly
      break;
    case 'every_30_days':
      monthlyCost = subscription.cost * (365 / 30) / 12; // Convert 30-day cycle to monthly
      break;
    case 'weekly':
      monthlyCost = subscription.cost * 4.33; // Average weeks per month
      break;
    case 'monthly':
    default:
      monthlyCost = subscription.cost; // Already monthly
      break;
  }
  
  // Convert to user's preferred currency
  const subscriptionCurrency = subscription.currency || 'USD';
  return convertCurrency(monthlyCost, subscriptionCurrency, userPreferredCurrency);
};

// Calculate total monthly spending in preferred currency
export const calculateTotalMonthlySpending = (
  subscriptions: Array<{ cost: number; currency?: string; billing_cycle: string }>,
  userPreferredCurrency: string
): number => {
  return subscriptions.reduce((total, subscription) => {
    return total + getMonthlyCostInCurrency(subscription, userPreferredCurrency);
  }, 0);
};

// Real-time exchange rate API using ExchangeRate-API
export const fetchRealTimeExchangeRate = async (
  fromCurrency: string, 
  toCurrency: string
): Promise<number> => {
  try {
    // Use ExchangeRate-API (free tier)
    const response = await fetch(`https://open.er-api.com/v6/latest/${fromCurrency}`);
    const data = await response.json();
    
    if (data.rates && data.rates[toCurrency]) {
      console.log(`Real-time rate: ${fromCurrency} to ${toCurrency} = ${data.rates[toCurrency]}`);
      return data.rates[toCurrency];
    } else {
      console.warn(`Rate not found for ${fromCurrency} to ${toCurrency}, using fallback`);
      return getExchangeRate(fromCurrency, toCurrency);
    }
  } catch (error) {
    console.error('Failed to fetch exchange rate:', error);
    return getExchangeRate(fromCurrency, toCurrency); // Fallback to cached rate
  }
};

// Background function to update exchange rates periodically
export const updateExchangeRatesInBackground = async () => {
  try {
    console.log('🔄 Updating exchange rates from API...');
    
    // Update USD rates
    const usdResponse = await fetch(`https://open.er-api.com/v6/latest/USD`);
    const usdData = await usdResponse.json();
    
    if (usdData.rates) {
      // Update the static rates with real-time data
      DEFAULT_EXCHANGE_RATES['USD_IDR'] = usdData.rates.IDR || DEFAULT_EXCHANGE_RATES['USD_IDR'];
      DEFAULT_EXCHANGE_RATES['USD_EUR'] = usdData.rates.EUR || DEFAULT_EXCHANGE_RATES['USD_EUR'];
      DEFAULT_EXCHANGE_RATES['USD_GBP'] = usdData.rates.GBP || DEFAULT_EXCHANGE_RATES['USD_GBP'];
      
      // Update reverse rates
      DEFAULT_EXCHANGE_RATES['IDR_USD'] = 1 / DEFAULT_EXCHANGE_RATES['USD_IDR'];
      DEFAULT_EXCHANGE_RATES['EUR_USD'] = 1 / DEFAULT_EXCHANGE_RATES['USD_EUR'];
      DEFAULT_EXCHANGE_RATES['GBP_USD'] = 1 / DEFAULT_EXCHANGE_RATES['USD_GBP'];
      
      console.log('✅ Exchange rates updated successfully!');
      console.log(`USD to IDR: ${DEFAULT_EXCHANGE_RATES['USD_IDR']}`);
    }
  } catch (error) {
    console.error('❌ Failed to update exchange rates:', error);
  }
}; 